import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FullPageState } from "./FullPageState";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <FullPageState
        icon={<AlertTriangle />}
        title="Something went wrong"
        description="An unexpected error interrupted this page. Try again, or head back to the start."
        actions={
          <>
            <Button onClick={this.handleReset}>
              <RotateCcw />
              Try again
            </Button>
            <Button variant="outline" asChild>
              <a href="/">
                <Home />
                Go home
              </a>
            </Button>
          </>
        }
      >
        {import.meta.env.DEV && this.state.error?.message && (
          <pre className="mt-6 max-h-40 overflow-auto rounded-md border border-border bg-card p-3 text-left font-mono text-code text-destructive">
            {this.state.error.message}
          </pre>
        )}
      </FullPageState>
    );
  }
}

export default ErrorBoundary;
